import { useEffect, useState } from 'react';
import axios from 'axios';
import { PlateScanButton } from './PlateScanner';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

export const normalizeArLicensePlate = (value: string) =>
  String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

export const isValidArLicensePlate = (value: string) => {
  const plate = normalizeArLicensePlate(value);
  return /^[A-Z]{3}\d{3}$/.test(plate) || /^[A-Z]{2}\d{3}[A-Z]{2}$/.test(plate);
};

type Resolution = 'same_owner' | 'new_owner';

// El backend devuelve 409 con esto cuando la patente ya está a nombre de otro y hace falta decidir.
interface Decision {
  kind: 'placeholder_history' | 'real_owner';
  vehicle: { id: string; licensePlate: string; make?: string | null; model?: string | null; year?: number | null };
  owner: { id: string; name: string; placeholder: boolean };
  history: { turnos: number; remitos: number; presupuestos: number; saldo: number };
}

interface VehicleCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string | null | undefined;
  /** Texto tipeado en el combobox al elegir "Crear vehiculo..." (prefill de patente) */
  initialPlate?: string;
  /** Se llama con el vehiculo creado o reasignado (respuesta del backend) */
  onCreated: (vehicle: any) => void | Promise<void>;
}

const fmtARS = (n: number) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);

const resumenHistoria = (h: Decision['history']) => {
  const partes: string[] = [];
  if (h.turnos) partes.push(`${h.turnos} turno${h.turnos === 1 ? '' : 's'}`);
  if (h.remitos) partes.push(`${h.remitos} remito${h.remitos === 1 ? '' : 's'}`);
  if (h.presupuestos) partes.push(`${h.presupuestos} presupuesto${h.presupuestos === 1 ? '' : 's'}`);
  if (h.saldo) partes.push(`saldo en cta cte de ${fmtARS(h.saldo)}`);
  return partes.join(', ');
};

/**
 * Modal compartido para crear un vehiculo de un cliente (Turnos, Remitos, Presupuestos, Diagnosticos).
 * Si la patente ya existe a nombre de otro, resuelve el cambio de dueño acá mismo.
 */
export default function VehicleCreateDialog({
  open,
  onOpenChange,
  clientId,
  initialPlate = '',
  onCreated
}: VehicleCreateDialogProps) {
  const [form, setForm] = useState({ licensePlate: '', make: '', model: '', year: '' });
  const [saving, setSaving] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [decision, setDecision] = useState<Decision | null>(null);

  useEffect(() => {
    if (open) {
      setForm({ licensePlate: normalizeArLicensePlate(initialPlate), make: '', model: '', year: '' });
      setDecision(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const lookupPlate = async (plateOverride?: string) => {
    const plate = normalizeArLicensePlate(plateOverride ?? form.licensePlate);
    if (!plate || !isValidArLicensePlate(plate)) return;
    setLookupLoading(true);
    try {
      const res = await axios.get(`/api/vehicles/lookup-plate/${plate}`);
      const d = res.data;
      setForm((prev) => ({
        ...prev,
        make: d.make || prev.make,
        model: d.model || prev.model,
        year: d.year ? String(d.year) : prev.year
      }));
      toast.success(`Datos cargados: ${d.make || ''} ${d.model || ''} ${d.year || ''}`);
    } catch (err: any) {
      if (err?.response?.status === 404) toast.info('Vehículo no encontrado en la base de datos');
      else toast.error(err?.response?.data?.error || 'Error consultando datos');
    } finally {
      setLookupLoading(false);
    }
  };

  const postVehicle = async (resolution?: Resolution) => {
    if (!clientId) {
      toast.error('Seleccioná un cliente primero');
      return;
    }

    const licensePlate = normalizeArLicensePlate(form.licensePlate);
    const model = String(form.model || '').trim();
    const make = String(form.make || '').trim();
    const yearStr = String(form.year || '').trim();

    if (!licensePlate) {
      toast.error('La patente es requerida');
      return;
    }
    if (!isValidArLicensePlate(licensePlate)) {
      toast.error('Formato de patente inválido. Ej: AAA123 o AB123CD');
      return;
    }
    if (!model) {
      toast.error('El modelo es requerido');
      return;
    }

    const currentYear = new Date().getFullYear();
    const parsedYear = yearStr ? Number(yearStr) : null;
    if (parsedYear !== null && (!Number.isFinite(parsedYear) || parsedYear < 1900 || parsedYear > currentYear)) {
      toast.error(`Año inválido. No puede ser mayor al actual (${currentYear}).`);
      return;
    }

    try {
      setSaving(true);
      const res = await axios.post('/api/vehicles', {
        clientId,
        licensePlate,
        make: make || null,
        model,
        year: parsedYear,
        resolution
      });
      const d = res.data || {};
      if (d.vehicle) {
        await onCreated(d.vehicle);
      }
      onOpenChange(false);
      if (d.reassigned && d.merged) {
        toast.success(`${licensePlate} asignado al cliente. Lo que estaba a nombre de "${d.fromOwner}" ahora es de este cliente.`);
      } else if (d.reassigned) {
        toast.success(`${licensePlate} pasó a este cliente. Los trabajos anteriores siguen a nombre de ${d.fromOwner}.`);
      } else if (d.existed) {
        toast.success('Esa patente ya estaba cargada: se usó la ficha existente');
      } else {
        toast.success('Vehículo creado');
      }
    } catch (error: any) {
      const data = error?.response?.data;
      if (error?.response?.status === 409 && data?.needsDecision) {
        setDecision(data as Decision);
        return;
      }
      console.error('Error creando vehiculo:', error);
      toast.error(data?.error || 'Error al crear vehículo');
    } finally {
      setSaving(false);
    }
  };

  const plateLabel = decision?.vehicle.licensePlate || normalizeArLicensePlate(form.licensePlate);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{decision ? 'Esta patente ya está cargada' : 'Crear vehículo'}</DialogTitle>
          <DialogDescription>
            {decision
              ? `${plateLabel} figura a nombre de ${decision.owner.name}.`
              : 'Cargá los datos para el cliente seleccionado.'}
          </DialogDescription>
        </DialogHeader>

        {decision ? (
          <div className="space-y-4">
            {decision.kind === 'placeholder_history' ? (
              <>
                <p className="text-sm">
                  Ese "Sin datos" tiene {resumenHistoria(decision.history)}. ¿Es la misma persona, que recién ahora
                  tiene datos, o el auto cambió de dueño?
                </p>
                <div className="grid gap-2">
                  <Button type="button" onClick={() => postVehicle('same_owner')} disabled={saving} className="h-auto whitespace-normal py-2">
                    Es el mismo dueño: pasar todo el historial a este cliente
                  </Button>
                  <Button type="button" variant="outline" onClick={() => postVehicle('new_owner')} disabled={saving} className="h-auto whitespace-normal py-2">
                    Cambió de dueño: pasar solo el auto
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Si cambió de dueño, la historia del vehículo (libreta, checklists, km) se conserva; los trabajos y
                  remitos anteriores quedan a nombre del dueño anterior.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm">
                  ¿Pasar el auto a este cliente?
                  {decision.history && resumenHistoria(decision.history)
                    ? ` ${decision.owner.name} tiene ${resumenHistoria(decision.history)} con este vehículo; eso queda en su ficha.`
                    : ''}
                </p>
                <Button type="button" onClick={() => postVehicle('new_owner')} disabled={saving} className="h-auto whitespace-normal py-2">
                  Sí, pasarlo a este cliente
                </Button>
                <p className="text-xs text-muted-foreground">
                  La historia del vehículo (libreta, checklists, km) se conserva. En la ficha de {decision.owner.name}
                  queda anotado el cambio.
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="vcd-license-plate">Patente *</Label>
              <div className="flex gap-2">
                <Input
                  id="vcd-license-plate"
                  value={form.licensePlate}
                  onChange={(e) => setForm((prev) => ({ ...prev, licensePlate: e.target.value.toUpperCase() }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      lookupPlate();
                    }
                  }}
                  placeholder="AAA123 o AB123CD"
                  className="flex-1"
                />
                <PlateScanButton
                  onPlate={(p) => {
                    setForm((prev) => ({ ...prev, licensePlate: p }));
                    lookupPlate(p);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={lookupLoading || !isValidArLicensePlate(form.licensePlate)}
                  onClick={() => lookupPlate()}
                >
                  {lookupLoading ? (
                    <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full inline-block" />
                  ) : 'Buscar'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Presioná Enter o "Buscar" para autocompletar marca, modelo y año.</p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="vcd-make">Marca</Label>
                <Input
                  id="vcd-make"
                  value={form.make}
                  onChange={(e) => setForm((prev) => ({ ...prev, make: e.target.value }))}
                  placeholder="Ej: Toyota"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vcd-year">Año</Label>
                <Input
                  id="vcd-year"
                  inputMode="numeric"
                  value={form.year}
                  onChange={(e) => setForm((prev) => ({ ...prev, year: e.target.value.replace(/[^0-9]/g, '') }))}
                  placeholder={`${new Date().getFullYear()}`}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="vcd-model">Modelo *</Label>
              <Input
                id="vcd-model"
                value={form.model}
                onChange={(e) => setForm((prev) => ({ ...prev, model: e.target.value }))}
                placeholder="Ej: Corolla"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {decision ? (
            <Button type="button" variant="outline" onClick={() => setDecision(null)} disabled={saving}>
              Volver
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button type="button" onClick={() => postVehicle()} disabled={saving}>
                {saving ? 'Creando...' : 'Crear vehículo'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
